const uuid=/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const maximumPreparedAudioBytes=230400044;
export function audioError(message){return Object.assign(new Error(message),{customerSafe:true});}
export function isAudioAsset(asset){return Number(asset?.playbackMode)===5||asset?.authoredPlayback==="audio";}
export function audioSettings(value){
  if(value==null)return null;
  if(!value||typeof value!=="object"||Array.isArray(value)||Object.keys(value).some(k=>!["assetId","volume"].includes(k))||
    !uuid.test(value.assetId??"")||/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(value.assetId)||
    !Number.isInteger(value.volume)||value.volume<0||value.volume>3)throw audioError("Choose valid saved audio and a volume from 0 to 3.");
  return {assetId:value.assetId.toLowerCase(),volume:value.volume};
}
export function backgroundAudioSettings(value){
  if(value==null)return null;
  if(typeof value!=="object"||Array.isArray(value)||Object.keys(value).some(k=>!["assetId","volume","loop"].includes(k))||typeof value.loop!=="boolean")
    throw audioError("Choose valid background music, volume and repeat settings.");
  return {...audioSettings({assetId:value.assetId,volume:value.volume}),loop:value.loop};
}
export function touchFeedbackSettings(value){
  if(value==null)return null;
  if(!value||typeof value!=="object"||Array.isArray(value)||Object.keys(value).some(k=>!["sound","volume"].includes(k))||
    ![0,1,2].includes(value.sound)||!Number.isInteger(value.volume)||value.volume<0||value.volume>3)
    throw audioError("Choose None, Click or Beep and a feedback volume from 0 to 3.");
  return {sound:value.sound,volume:value.volume};
}
export function audioFields(item){
  return {...(item?.backgroundAudio!=null?{backgroundAudio:backgroundAudioSettings(item.backgroundAudio)}:{}),
    ...(item?.audio!=null?{audio:audioSettings(item.audio)}:{}),
    ...(item?.touchFeedback!=null?{touchFeedback:touchFeedbackSettings(item.touchFeedback)}:{})};
}
export function playlistAudioReferences(items){
  const refs=new Set();
  for(const item of items??[]){
    const audio=audioSettings(item?.audio);if(audio)refs.add(audio.assetId);
    const background=backgroundAudioSettings(item?.backgroundAudio);if(background)refs.add(background.assetId);
    touchFeedbackSettings(item?.touchFeedback);
    for(const area of item?.interactions?.touchAreas??[]){const clip=audioSettings(area?.audio);if(clip)refs.add(clip.assetId);}
  }
  return [...refs];
}
export function validatePlaylistAudio(items,assets){
  const byId=assets==null?null:new Map(assets.map(asset=>[String(asset.assetId??asset.id).toLowerCase(),asset]));
  for(const item of items??[]){
    if(byId&&isAudioAsset(byId.get(String(item?.assetId).toLowerCase())))throw audioError("Audio must be attached to artwork, not used as an image.");
    for(const area of item?.interactions?.touchAreas??[]){
      if((Number(area?.action?.kind)===7||area?.action?.kind==="keep-artwork")&&!area.audio)
        throw audioError("Choose audio to play while keeping the current artwork.");
      const target=area?.action?.targetContentId??area?.action?.targetAssetId;
      if(target&&byId&&isAudioAsset(byId.get(String(target).toLowerCase())))throw audioError("Choose artwork for a hotspot destination and attach sound separately.");
    }
    const refs=playlistAudioReferences([item]);
    if((refs.length||Number(item?.touchFeedback?.sound)>0)&&String(item?.type).toLowerCase()==="video")
      throw audioError("Audio attachments and touch sounds are supported on Fit and Scroll artwork.");
    for(const id of refs)if(byId&&!isAudioAsset(byId.get(id)))throw audioError("A playlist audio clip is missing from this account. Import it or remove its assignment before saving or publishing.");
  }
  const refs=playlistAudioReferences(items);
  if(refs.length>64)throw audioError("A playlist supports up to 64 different audio clips.");
  return refs;
}
export function validatePreparedAudio(bytes){
  if(!(bytes instanceof Uint8Array)||bytes.length<46||bytes.length>230500000)
    throw audioError("Prepared audio must contain up to two hours of mono sound.");
  const view=new DataView(bytes.buffer,bytes.byteOffset,bytes.byteLength);
  const tag=(offset)=>String.fromCharCode(...bytes.subarray(offset,offset+4));
  const invalid=()=>{throw audioError("Audio must be verified Pirdy mono 16 kHz PCM16 WAV. Import the original file to convert it.");};
  if(tag(0)!=="RIFF"||tag(8)!=="WAVE"||view.getUint32(4,true)!==bytes.length-8)invalid();
  let fmt=false,data=null,offset=12;
  while(offset+8<=bytes.length){
    const kind=tag(offset),size=view.getUint32(offset+4,true),start=offset+8;
    if(size>bytes.length-start)invalid();
    if(kind==="fmt "){
      if(fmt||size<16||view.getUint16(start,true)!==1||view.getUint16(start+2,true)!==1||
        view.getUint32(start+4,true)!==16000||view.getUint32(start+8,true)!==32000||
        view.getUint16(start+12,true)!==2||view.getUint16(start+14,true)!==16)invalid();
      fmt=true;
    }else if(kind==="data"){if(data!==null||size<2||size%2||size>230400000)invalid();data=size;}
    offset=start+size+(size%2);
  }
  if(!fmt||data===null||offset!==bytes.length)invalid();
  return data/32000;
}
export function audioDuration(seconds){
  const ticks=Math.round(seconds*10000000),hours=Math.floor(ticks/36000000000),minutes=Math.floor(ticks/600000000)%60,
    whole=Math.floor(ticks/10000000)%60,fraction=ticks%10000000;
  return [hours,minutes,whole].map(v=>String(v).padStart(2,"0")).join(":")+"."+String(fraction).padStart(7,"0");
}
export function preparedAudioMetadata(audio,name,crc32){
  const seconds=validatePreparedAudio(audio.bytes);
  return {name:String(name??"Audio").replace(/\.[^.]+$/,"").trim().slice(0,120)||"Audio",
    sha256:audio.hash,fileSize:audio.bytes.length,width:0,height:0,orientation:2,playbackMode:5,pixelFormat:2,
    crc32:crc32(audio.bytes),extension:".wav",durationSeconds:seconds};
}
